import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import { useAthleteStore } from '@/store';
import { MatchResult, MatchMethod, type Match, type MatchStats } from '@/types';
import { ChevronLeft, Save, Plus, Minus } from 'lucide-react';

// Reusable Counter component for stats
function StatCounter({ label, value, onChange }: { label: string, value: number, onChange: (v: number) => void }) {
    return (
        <div className="flex items-center justify-between bg-bg-surface border border-white/5 rounded-xl p-3">
            <span className="text-white text-sm font-bold">{label}</span>
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => onChange(Math.max(0, value - 1))}
                    className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10"
                >
                    <Minus size={14} />
                </button>
                <span className="w-6 text-center text-white font-black">{value}</span>
                <button
                    type="button"
                    onClick={() => onChange(value + 1)}
                    className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-gold-500/20 hover:text-gold-500"
                >
                    <Plus size={14} />
                </button>
            </div>
        </div>
    );
}

export function MatchFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const activeId: string = useAthleteStore((s) => s.activeId) ?? '';
    const isEdit = Boolean(id);

    // Form State
    const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0] ?? '');
    const [opponentName, setOpponentName] = useState('');
    const [matchEvent, setMatchEvent] = useState('');
    const [weightClass, setWeightClass] = useState('');
    const [result, setResult] = useState<MatchResult>('Win');
    const [method, setMethod] = useState<MatchMethod>('Decision');
    const [myScore, setMyScore] = useState(0);
    const [opponentScore, setOpponentScore] = useState(0);
    const [notes, setNotes] = useState('');

    // Detailed Stats State
    const [takedowns, setTakedowns] = useState(0);
    const [takedownAttempts, setTakedownAttempts] = useState(0);
    const [escapes, setEscapes] = useState(0);
    const [reversals, setReversals] = useState(0);
    const [nearFalls, setNearFalls] = useState(0);
    const [penalties, setPenalties] = useState(0);

    const [showDetailedStats, setShowDetailedStats] = useState(false);

    // Load existing data if editing
    useLiveQuery(async () => {
        if (isEdit && id) {
            const matchIdNum = Number(id);
            const match = await db.matches.get(matchIdNum);
            if (match) {
                setDate(match.date ?? new Date().toISOString().split('T')[0] ?? '');
                setOpponentName(match.opponentName);
                setMatchEvent(match.eventName || '');
                setWeightClass(match.weightClass || '');
                setResult(match.result);
                setMethod(match.method);
                setMyScore(match.myScore);
                setOpponentScore(match.opponentScore);
                setNotes(match.notes || '');
            }

            const stats = await db.matchStats.where('matchId').equals(matchIdNum).first();
            if (stats) {
                setTakedowns(stats.takedowns || 0);
                setTakedownAttempts(stats.takedown_attempts || 0);
                setEscapes(stats.escapes || 0);
                setReversals(stats.reversals || 0);
                setNearFalls(stats.near_fall_points || 0);
                setPenalties(stats.penalties || 0);
                setShowDetailedStats(true);
            }
        }
    }, [id, isEdit]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const matchData = {
            athleteId: activeId,
            date,
            opponentName,
            ...(matchEvent ? { eventName: matchEvent } : {}),
            ...(weightClass ? { weightClass } : {}),
            result,
            method,
            myScore: Number(myScore),
            opponentScore: Number(opponentScore),
            ...(notes ? { notes } : {}),
            createdAt: new Date().toISOString()
        } as Match; // omit ID

        let finalMatchId: number;

        if (isEdit && id) {
            finalMatchId = Number(id);
            await db.matches.update(finalMatchId, matchData);
        } else {
            finalMatchId = await db.matches.add(matchData);
        }

        if (showDetailedStats) {
            const statsData = {
                matchId: finalMatchId,
                takedowns,
                takedown_attempts: takedownAttempts,
                escapes,
                reversals,
                near_fall_points: nearFalls,
                penalties
            } as MatchStats; // omit id

            const existingStats = await db.matchStats.where('matchId').equals(finalMatchId).first();
            if (existingStats && existingStats.id) {
                await db.matchStats.update(existingStats.id, statsData);
            } else {
                await db.matchStats.add(statsData);
            }
        }

        navigate('/matches');
    };

    return (
        <div className="min-h-screen bg-bg pb-24 animate-fade-in">
            {/* Header */}
            <div className="bg-bg-surface border-b border-white/5 pt-12 pb-4 px-4 sticky top-0 z-10 backdrop-blur-md bg-bg-surface/80">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-text-muted hover:text-white transition-colors"
                        aria-label="Go back"
                    >
                        <ChevronLeft className="w-6 h-6" aria-hidden />
                    </button>
                    <h1 className="text-xl font-bold text-white">
                        {isEdit ? 'Edit Match' : 'Log New Match'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6 max-w-lg mx-auto">
                {/* 1. Date & Event */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Date</label>
                        <input
                            type="date"
                            required
                            value={date}
                            onChange={e => setDate(e.target.value)}
                            className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors"
                        />
                    </div>
                </div>

                {/* 2. Opponent */}
                <div>
                    <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Opponent</label>
                    <input
                        type="text"
                        required
                        placeholder="Opponent Name / School"
                        value={opponentName}
                        onChange={e => setOpponentName(e.target.value)}
                        className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors"
                    />
                </div>

                {/* 3. Result & Method */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Result</label>
                        <select
                            value={result}
                            onChange={e => setResult(e.target.value as MatchResult)}
                            className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors appearance-none"
                        >
                            <option value="Win">Win 🏆</option>
                            <option value="Loss">Loss ❌</option>
                            <option value="Draw">Draw 🤝</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Method</label>
                        <select
                            value={method}
                            onChange={e => setMethod(e.target.value as MatchMethod)}
                            className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold-base transition-colors appearance-none"
                        >
                            <option value="Decision">Decision (Dec)</option>
                            <option value="Major">Major Dec (MD)</option>
                            <option value="Tech Fall">Tech Fall (TF)</option>
                            <option value="Pin">Pin (Fall)</option>
                            <option value="DQ">DQ</option>
                            <option value="Injury">Injury</option>
                            <option value="Forfeit">Forfeit</option>
                        </select>
                    </div>
                </div>

                {/* 4. Score */}
                <div>
                    <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Final Score</label>
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <label className="block text-[10px] text-text-muted mb-1 text-center">My Score</label>
                            <input
                                type="number"
                                min={0}
                                value={myScore}
                                onChange={e => setMyScore(Number(e.target.value))}
                                className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white text-center font-bold text-xl focus:outline-none focus:border-gold-base transition-colors"
                            />
                        </div>
                        <span className="text-text-muted font-bold">-</span>
                        <div className="flex-1">
                            <label className="block text-[10px] text-text-muted mb-1 text-center">Opponent</label>
                            <input
                                type="number"
                                min={0}
                                value={opponentScore}
                                onChange={e => setOpponentScore(Number(e.target.value))}
                                className="w-full bg-bg-elevated border border-white/10 rounded-xl px-4 py-3 text-white text-center font-bold text-xl focus:outline-none focus:border-gold-base transition-colors"
                            />
                        </div>
                    </div>
                </div>

                {/* Detailed Stats Toggle */}
                <div className="pt-4 border-t border-white/5">
                    <button
                        type="button"
                        onClick={() => setShowDetailedStats(!showDetailedStats)}
                        className="text-sm font-bold text-gold-500 flex items-center gap-2 uppercase tracking-widest"
                    >
                        {showDetailedStats ? '- Hide Details' : '+ Add Detailed Stats'}
                    </button>

                    {showDetailedStats && (
                        <div className="mt-4 space-y-3 animate-fade-in-up">
                            <StatCounter label="Takedowns" value={takedowns} onChange={setTakedowns} />
                            <StatCounter label="Takedown Attempts" value={takedownAttempts} onChange={setTakedownAttempts} />
                            <StatCounter label="Escapes" value={escapes} onChange={setEscapes} />
                            <StatCounter label="Reversals" value={reversals} onChange={setReversals} />
                            <StatCounter label="Near Fall Points" value={nearFalls} onChange={setNearFalls} />
                            <StatCounter label="Penalties" value={penalties} onChange={setPenalties} />
                        </div>
                    )}
                </div>

                {/* Submit Action */}
                <button
                    type="submit"
                    className="w-full mt-8 py-4 rounded-xl bg-gold-base text-bg-900 font-bold text-lg shadow-[0_4px_20px_rgba(212,175,55,0.3)] hover:shadow-[0_4px_28px_rgba(212,175,55,0.45)] transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                    <Save className="w-5 h-5" />
                    {isEdit ? 'Update Match' : 'Save Match'}
                </button>
            </form>
        </div>
    );
}
